/**
 * Climb Page
 *
 * Shows routes for the active gym with progress tracking and view toggle.
 * Features:
 * - Floating action buttons for gym logo and filter
 * - Content area with map or list view
 * - Bottom bar with progress ring and view toggles
 */

import {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
  type CSSProperties,
} from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import FilterListIcon from '@mui/icons-material/FilterList';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SortIcon from '@mui/icons-material/Sort';
import { useActiveGym } from '../hooks/useActiveGym';
import { useGymStore } from '../stores/gymStore';
import { useGymMemberships } from '../hooks/useGymMemberships';
import { useGymProgress } from '../hooks/useGymProgress';
import { useRoutes } from '../hooks/useRoutes';
import { useRouteCompletionStatus } from '../hooks/useRouteCompletionStatus';
import { useClimbLogs } from '../hooks/useClimbLogs';
import { useAuth } from '../hooks/useAuth';
import { getFirebaseDb } from '../lib/firebase';
import { collection, query, where, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { GymLogoPopover } from '../components/gym/GymLogoPopover';
import {
  ClimbBottomBar,
  ClimbMapView,
  RouteBottomSheet,
  RouteListView,
  FilterSheet,
  MapLegendModal,
  DEFAULT_FILTERS,
  SORT_OPTIONS,
  type ViewMode,
  type RouteFilters,
  type SortOption,
} from '../components/climb';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { SpotlightOverlay, useOnboarding, type SpotlightStep } from '../components/onboarding';
import type { ClimbOutcome } from '@flash/shared';
import { getGradeIndex } from '@flash/shared';

// Default gym ID - Apex is the only gym for now
const DEFAULT_GYM_ID = 'apex-climbing';

export function ClimbPage() {
  const { user } = useAuth();
  const { activeGym, activeGymId, hasActiveGym, loading, setActiveGym } = useActiveGym();
  const { memberships } = useGymMemberships();

  // Auto-set Apex as the active gym if none is set
  useEffect(() => {
    if (!loading && !hasActiveGym) {
      setActiveGym(DEFAULT_GYM_ID);
    }
  }, [loading, hasActiveGym, setActiveGym]);

  // Check if user is owner for this gym - needed for gym settings
  const isOwner = activeGymId
    ? memberships.some((m) => m.gymId === activeGymId && m.role === 'owner')
    : false;

  // Check if user can manage routes (setter or owner) - needed for showing drafts and creating routes
  const canManageRoutes = activeGymId
    ? memberships.some(
        (m) => m.gymId === activeGymId && (m.role === 'setter' || m.role === 'owner')
      )
    : false;

  const {
    sendsCount,
    totalRoutes,
    progressPercent,
    loading: progressLoading,
  } = useGymProgress(activeGymId ?? undefined);
  // Show draft routes to admins (setters/owners)
  const {
    routes,
    loading: routesLoading,
    setFilters: setRouteFilters,
  } = useRoutes(activeGymId ?? undefined, {
    initialFilters: { includeDrafts: canManageRoutes },
  });
  const { sentRouteIds, sendLogIds } = useRouteCompletionStatus(activeGymId ?? undefined);
  const { addClimbLog, deleteClimbLog } = useClimbLogs({
    initialFilters: { gymId: activeGymId ?? undefined },
  });

  // Track last visited path for returning users
  const location = useLocation();
  const setLastVisitedPath = useGymStore((state) => state.setLastVisitedPath);
  useEffect(() => {
    setLastVisitedPath(location.pathname + location.search);
  }, [location.pathname, location.search, setLastVisitedPath]);

  // Update route filters when admin status changes (e.g., when memberships load)
  useEffect(() => {
    setRouteFilters({ includeDrafts: canManageRoutes });
  }, [canManageRoutes, setRouteFilters]);

  // View mode state - persisted to localStorage, synced with URL
  const VIEW_MODE_STORAGE_KEY = 'flash:climbViewMode';
  const [searchParams, setSearchParams] = useSearchParams();
  const viewParam = searchParams.get('view');

  // Determine view mode: URL param takes precedence, then localStorage, then default to 'map'
  const viewMode: ViewMode = useMemo(() => {
    if (viewParam === 'map' || viewParam === 'list') {
      return viewParam;
    }
    // No URL param - check localStorage
    const stored = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    return stored === 'list' ? 'list' : 'map';
  }, [viewParam]);

  // Sync localStorage when viewMode changes (handles URL param visits)
  useEffect(() => {
    localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
  }, [viewMode]);

  // Sync URL with stored preference on mount (when no URL param)
  useEffect(() => {
    if (!viewParam) {
      const stored = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
      if (stored === 'map' || stored === 'list') {
        setSearchParams({ view: stored }, { replace: true });
      }
    }
  }, [viewParam, setSearchParams]);

  const setViewMode = useCallback(
    (mode: ViewMode) => {
      setSearchParams({ view: mode }, { replace: true });
    },
    [setSearchParams]
  );

  // Selected route ID for bottom sheet (store ID, derive route from routes array for fresh data)
  const [selectedRouteId, setSelectedRouteId] = useState<string | null>(null);

  // Derive the current route from the routes array (updates when routes change from Firestore)
  const selectedRoute = useMemo(
    () => (selectedRouteId ? (routes.find((r) => r.id === selectedRouteId) ?? null) : null),
    [routes, selectedRouteId]
  );

  // Climb logging state
  const [isLoggingClimb, setIsLoggingClimb] = useState(false);

  // Filter state
  const [filters, setFilters] = useState<RouteFilters>(DEFAULT_FILTERS);
  const [showFilters, setShowFilters] = useState(false);

  // Sort state (only used in list view)
  const [sortOption, setSortOption] = useState<SortOption>('default');
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  // Legend modal state
  const [showLegend, setShowLegend] = useState(false);

  // Legend tooltip state - shows once for first-time map visitors
  const LEGEND_TOOLTIP_SEEN_KEY = 'flash:legendTooltipSeen';
  const [showLegendTooltip, setShowLegendTooltip] = useState(false);

  // Onboarding state
  const { isOnboarding, currentPhase, currentStep, nextStep, nextPhase, completeOnboarding } =
    useOnboarding();

  // Onboarding steps for the Climb page (9 steps total)
  const climbOnboardingSteps: SpotlightStep[] = useMemo(
    () => [
      // LIST VIEW STEPS (0-4)
      {
        id: 'routes',
        targetSelector: 'routes-content',
        title: 'Your Routes',
        description:
          "Each colored circle shows a route's grade. Tap any route to see details and log your climbs.",
        tooltipPosition: 'bottom',
        padding: 0,
      },
      {
        id: 'progress',
        targetSelector: 'progress-ring',
        title: 'Track Your Progress',
        description:
          'This ring fills as you send routes. Tap to see all circuits or just your current one.',
        tooltipPosition: 'top',
        padding: 8,
      },
      {
        id: 'sort',
        targetSelector: 'sort-button',
        title: 'Sort Routes',
        description: 'Change how routes are ordered—by grade, date set, or your progress.',
        tooltipPosition: 'bottom',
        padding: 8,
      },
      {
        id: 'filter',
        targetSelector: 'filter-button',
        title: 'Filter Routes',
        description: 'Focus on specific grades, colors, or areas of the gym.',
        tooltipPosition: 'bottom',
        padding: 8,
      },
      {
        id: 'view-toggle',
        targetSelector: 'view-toggle',
        title: 'Try Map View',
        description: "See routes on the gym's floor plan. Tap to switch!",
        tooltipPosition: 'top',
        padding: 8,
        hideActionButton: true, // Force user to tap toggle
      },
      // MAP VIEW STEPS (5-6) - after view switch
      {
        id: 'map-intro',
        targetSelector: 'map-welcome', // Non-existent, centers tooltip
        title: 'Welcome to Map View',
        description: 'Routes appear as pins on the floor plan. Tap any pin to see route details.',
        tooltipPosition: 'bottom',
      },
      {
        id: 'legend',
        targetSelector: 'legend-button',
        title: 'Color Legend',
        description:
          'Each gym uses different colors for grades. Tap the help icon to see what they mean!',
        tooltipPosition: 'left',
        padding: 8,
        hideActionButton: true, // Force user to open legend
      },
      // AFTER LEGEND STEPS (7-8)
      {
        id: 'legend-done',
        targetSelector: 'legend-done', // Non-existent, centers
        title: 'Now You Know!',
        description:
          'You can always tap the ? icon to check the color meanings. Ready to explore your profile?',
        tooltipPosition: 'bottom',
      },
      {
        id: 'go-profile',
        targetSelector: 'nav-tab-profile', // Bottom nav profile link
        title: 'Check Your Profile',
        description: 'Tap the Profile tab to see your climb history and settings!',
        tooltipPosition: 'top',
        padding: 8,
        hideActionButton: true, // Force user to tap Profile tab
      },
    ],
    []
  );

  // Check if we should show onboarding on this page
  const showClimbOnboarding = isOnboarding && currentPhase === 'climb';

  // Track view mode changes for onboarding (step 4 → 5)
  const prevViewModeRef = useRef(viewMode);
  useEffect(() => {
    if (
      showClimbOnboarding &&
      currentStep === 4 &&
      prevViewModeRef.current === 'list' &&
      viewMode === 'map'
    ) {
      nextStep(); // Move to step 5 (map view intro)
    }
    prevViewModeRef.current = viewMode;
  }, [viewMode, showClimbOnboarding, currentStep, nextStep]);

  // Track legend modal opening for onboarding (step 6 → 7)
  useEffect(() => {
    if (showClimbOnboarding && currentStep === 6 && showLegend) {
      // Wait for modal to be visible, then advance
      const timer = setTimeout(() => nextStep(), 500);
      return () => clearTimeout(timer);
    }
  }, [showLegend, showClimbOnboarding, currentStep, nextStep]);

  // Track navigation to profile tab for onboarding (step 8 → next phase)
  useEffect(() => {
    if (showClimbOnboarding && currentStep === 8 && location.pathname.startsWith('/profile')) {
      nextPhase(); // Move to profile phase
    }
  }, [location.pathname, showClimbOnboarding, currentStep, nextPhase]);

  // Hide spotlight overlay when legend modal is open
  const spotlightVisible = showClimbOnboarding && !showLegend;

  // Map container ref for measuring dimensions
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [mapDimensions, setMapDimensions] = useState({ width: 0, height: 0 });

  // Measure container dimensions synchronously to avoid flash of "No floor plan"
  // useLayoutEffect ensures measurement happens before paint when navigating back to page
  // Dependencies include loading/hasActiveGym because the container ref isn't available until data loads
  useLayoutEffect(() => {
    const container = mapContainerRef.current;
    if (!container) return;

    setMapDimensions({
      width: container.clientWidth,
      height: container.clientHeight,
    });
  }, [viewMode, loading, hasActiveGym]);

  // Listen for resize changes after initial measurement
  useEffect(() => {
    const container = mapContainerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setMapDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [viewMode]);

  // Check if gym has a floor plan for map view
  const hasFloorPlan = Boolean(activeGym?.floorPlan?.imageUrl);

  // Show legend tooltip on map view until user clicks the legend
  useEffect(() => {
    const hasSeenLegend = localStorage.getItem(LEGEND_TOOLTIP_SEEN_KEY);
    if (viewMode === 'map' && hasFloorPlan && !loading && !hasSeenLegend) {
      // Small delay so the map renders first
      const showTimer = setTimeout(() => {
        setShowLegendTooltip(true);
      }, 500);

      return () => clearTimeout(showTimer);
    } else {
      // Hide tooltip when leaving map view
      setShowLegendTooltip(false);
    }
  }, [viewMode, hasFloorPlan, loading]);

  // Only dismiss tooltip when user clicks the legend button
  const handleLegendClick = useCallback(() => {
    setShowLegendTooltip(false);
    localStorage.setItem(LEGEND_TOOLTIP_SEEN_KEY, 'true');
    setShowLegend(true);
  }, []);

  // Extract unique wall sections from routes
  const wallSections = useMemo(() => {
    const sections = new Set<string>();
    routes.forEach((route) => {
      if (route.wallSection) {
        sections.add(route.wallSection);
      }
    });
    return Array.from(sections).sort();
  }, [routes]);

  // Filter routes based on current filters
  const filteredRoutes = useMemo(() => {
    return routes.filter((route) => {
      // Grade filter - only apply if route has an officialGrade
      // Routes without officialGrade (color-only gyms) are always included in grade filter
      if (route.officialGrade) {
        const gradeIndex = getGradeIndex(route.officialGrade);
        if (gradeIndex < filters.minGrade || gradeIndex > filters.maxGrade) {
          return false;
        }
      }

      // Wall section filter
      if (filters.wallSections.length > 0 && !filters.wallSections.includes(route.wallSection)) {
        return false;
      }

      // Hide completed filter
      if (filters.hideCompleted && sentRouteIds.has(route.id)) {
        return false;
      }

      return true;
    });
  }, [routes, filters, sentRouteIds]);

  // Handle view mode change
  const handleViewModeChange = (mode: ViewMode) => {
    if (mode === 'map' && !hasFloorPlan) return;
    setViewMode(mode);
  };

  // Handle logging a climb from the bottom sheet
  const handleLogClimb = useCallback(
    async (routeId: string, outcome: ClimbOutcome, count: number = 1) => {
      if (!activeGymId) return;

      setIsLoggingClimb(true);
      try {
        // Log multiple attempts if count > 1
        const logPromises = Array.from({ length: count }, () =>
          addClimbLog({
            routeId,
            gymId: activeGymId,
            outcome,
            loggedAt: new Date(),
          })
        );
        await Promise.all(logPromises);
      } finally {
        setIsLoggingClimb(false);
      }
    },
    [activeGymId, addClimbLog]
  );

  // Handle unsending a route (delete the send log)
  const handleUnsend = useCallback(
    async (routeId: string) => {
      const sendLogId = sendLogIds.get(routeId);
      if (!sendLogId) return;

      setIsLoggingClimb(true);
      try {
        await deleteClimbLog(sendLogId);
      } finally {
        setIsLoggingClimb(false);
      }
    },
    [sendLogIds, deleteClimbLog]
  );

  // Handle deleting all climb data for a route
  const handleDeleteClimbData = useCallback(
    async (routeId: string) => {
      if (!user) return;

      const db = getFirebaseDb();
      const logsRef = collection(db, 'climbLogs');
      const logsQuery = query(
        logsRef,
        where('userId', '==', user.uid),
        where('routeId', '==', routeId)
      );

      const snapshot = await getDocs(logsQuery);
      const deletePromises = snapshot.docs.map((docSnap) =>
        deleteDoc(doc(db, 'climbLogs', docSnap.id))
      );
      await Promise.all(deletePromises);
    },
    [user]
  );

  // Close the route bottom sheet
  const handleCloseRouteSheet = useCallback(() => {
    setSelectedRouteId(null);
  }, []);

  // Check if filters are active
  const hasActiveFilters =
    filters.minGrade > 0 ||
    filters.maxGrade < 17 ||
    filters.wallSections.length > 0 ||
    filters.hideCompleted;

  // Show welcome state if no active gym is set
  if (!hasActiveGym) {
    return (
      <div style={containerStyle}>
        <div style={welcomeStateStyle}>
          <div style={welcomeIconStyle}>🧗</div>
          <h2 style={welcomeHeadingStyle}>Welcome to Flash!</h2>
          <p style={welcomeTextStyle}>
            Select a gym to start tracking your climbing progress and see routes on the map.
          </p>
          <Link to="/gyms" style={selectGymButtonStyle}>
            Browse Gyms
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={loadingContainerStyle}>
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (!activeGym) {
    return (
      <div style={containerStyle}>
        <div style={emptyStateStyle}>
          <h2>Gym not found</h2>
          <p>The selected gym could not be loaded</p>
          <Link to="/gyms" style={selectGymLinkStyle}>
            Browse gyms
          </Link>
        </div>
      </div>
    );
  }

  const isMapView = viewMode === 'map';

  // Unified layout - same floating elements for both views
  return (
    <div style={containerStyle}>
      {/* Content Area - Map or List */}
      <div id="routes-content" ref={mapContainerRef} style={contentAreaStyle}>
        {isMapView ? (
          // Map View - Full bleed
          !activeGym.floorPlan ? (
            // Gym has no floor plan configured
            <p style={emptyRoutesStyle}>No floor plan available</p>
          ) : (
            <ClimbMapView
              floorPlan={activeGym.floorPlan}
              routes={filteredRoutes}
              colorGrades={activeGym.colorGrades}
              tagColorGrades={activeGym.tagColorGrades}
              primaryMethod={activeGym.labelingMethods?.primaryMethod ?? 'holdColors'}
              sentRouteIds={sentRouteIds}
              containerWidth={mapDimensions.width}
              containerHeight={mapDimensions.height}
              {...(selectedRoute ? { selectedRouteId: selectedRoute.id } : {})}
              onRouteSelect={(route) => setSelectedRouteId(route.id)}
              animate
            />
          )
        ) : (
          // List View - Scrollable with padding for overlays
          <div style={listScrollContainerStyle}>
            {routesLoading ? (
              <div style={loadingContainerStyle}>
                <LoadingSpinner />
              </div>
            ) : filteredRoutes.length === 0 ? (
              <p style={emptyRoutesStyle}>
                {routes.length === 0 ? 'No routes at this gym yet' : 'No routes match your filters'}
              </p>
            ) : (
              <RouteListView
                routes={filteredRoutes}
                colorGrades={activeGym.colorGrades}
                tagColorGrades={activeGym.tagColorGrades}
                primaryMethod={activeGym.labelingMethods?.primaryMethod ?? 'holdColors'}
                sentRouteIds={sentRouteIds}
                onRouteSelect={(route) => setSelectedRouteId(route.id)}
                sortOption={sortOption}
                {...(selectedRoute ? { selectedRouteId: selectedRoute.id } : {})}
              />
            )}
          </div>
        )}
      </div>

      {/* Floating Action Buttons - Top (same position for both views) */}
      <div style={floatingTopStyle}>
        <div style={fabContainerStyle}>
          <GymLogoPopover
            gym={activeGym}
            canAccessSettings={isOwner}
            canCreateRoutes={canManageRoutes}
            floatingStyle
          />
        </div>
        <div style={rightButtonStackStyle}>
          {/* Sort pill - only in list view */}
          {!isMapView && (
            <div style={sortPillContainerStyle}>
              <button
                id="sort-button"
                style={{
                  ...sortPillStyle,
                  ...(sortOption !== 'default' ? sortPillActiveStyle : {}),
                }}
                aria-label="Sort routes"
                onClick={() => setShowSortDropdown(!showSortDropdown)}
              >
                <SortIcon style={sortPillIconStyle} />
                <span style={sortPillLabelStyle}>
                  {SORT_OPTIONS.find((o) => o.value === sortOption)?.label ?? 'Sort'}
                </span>
              </button>
              {/* Sort Dropdown */}
              {showSortDropdown && (
                <>
                  <div
                    style={sortDropdownOverlayStyle}
                    onClick={() => setShowSortDropdown(false)}
                    aria-hidden="true"
                  />
                  <ul style={sortDropdownStyle} role="listbox">
                    {SORT_OPTIONS.map((option) => (
                      <li key={option.value}>
                        <button
                          style={{
                            ...sortDropdownItemStyle,
                            ...(sortOption === option.value ? sortDropdownItemActiveStyle : {}),
                          }}
                          onClick={() => {
                            setSortOption(option.value);
                            setShowSortDropdown(false);
                          }}
                          role="option"
                          aria-selected={sortOption === option.value}
                        >
                          {option.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
          {/* Legend FAB - only in map view, left of filter */}
          {isMapView && (
            <div style={legendFabContainerStyle}>
              <button
                id="legend-button"
                style={fabStyle}
                aria-label="Map legend"
                onClick={handleLegendClick}
              >
                <HelpOutlineIcon />
              </button>
              {/* One-time tooltip pointing to legend button */}
              {showLegendTooltip && (
                <div style={legendTooltipStyle}>
                  <span style={legendTooltipTextStyle}>Tap here to see the map legend</span>
                  <div style={legendTooltipArrowStyle} />
                </div>
              )}
            </div>
          )}
          {/* Filter FAB - always rightmost */}
          <button
            id="filter-button"
            style={{
              ...fabStyle,
              ...(hasActiveFilters ? fabActiveStyle : {}),
            }}
            aria-label="Filter routes"
            onClick={() => setShowFilters(true)}
          >
            <FilterListIcon />
          </button>
        </div>
      </div>

      {/* Bottom Bar (same position for both views) */}
      <ClimbBottomBar
        sendsCount={sendsCount}
        totalRoutes={totalRoutes}
        progressPercent={progressPercent}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        hasFloorPlan={hasFloorPlan}
        loading={progressLoading}
        isRouteSelected={!!selectedRoute}
      />

      {/* Route Bottom Sheet */}
      {selectedRoute && (
        <RouteBottomSheet
          route={selectedRoute}
          colorGrades={activeGym.colorGrades}
          isSent={sentRouteIds.has(selectedRoute.id)}
          onClose={handleCloseRouteSheet}
          onLogClimb={handleLogClimb}
          onUnsend={handleUnsend}
          onDeleteClimbData={handleDeleteClimbData}
          isLogging={isLoggingClimb}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
          hasFloorPlan={hasFloorPlan}
        />
      )}

      {/* Filter Sheet */}
      {showFilters && (
        <FilterSheet
          routes={routes}
          wallSections={wallSections}
          filters={filters}
          onFilterChange={setFilters}
          onClose={() => setShowFilters(false)}
        />
      )}

      {/* Map Legend Modal */}
      {showLegend && (
        <MapLegendModal onClose={() => setShowLegend(false)} colorGrades={activeGym.colorGrades} />
      )}

      {/* Spotlight Onboarding for first-time users */}
      <SpotlightOverlay
        steps={climbOnboardingSteps}
        currentStep={currentStep}
        isVisible={spotlightVisible}
        onNext={nextStep}
        onSkip={completeOnboarding}
        onComplete={nextPhase}
      />
    </div>
  );
}

// ==================== LAYOUT STYLES ====================

// Main container - full height with relative positioning for overlays
const containerStyle: CSSProperties = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflow: 'hidden',
};

// Content area - fills available space
const contentAreaStyle: CSSProperties = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  backgroundColor: 'var(--color-bg-secondary)',
};

// List scroll container - scrollable, content scrolls under floating elements
const listScrollContainerStyle: CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
  paddingTop: 68, // Space for floating header (12 top + 48 button + 8 gap)
  paddingBottom: 80, // Just enough to scroll last items above floating elements
  backgroundColor: 'var(--color-bg-primary)',
};

// ==================== FLOATING ELEMENT STYLES ====================

// Floating top area for FABs
const floatingTopStyle: CSSProperties = {
  position: 'absolute',
  top: 12,
  left: 16,
  right: 16,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  zIndex: 20,
  pointerEvents: 'none',
};

// Container for FAB to enable pointer events
const fabContainerStyle: CSSProperties = {
  pointerEvents: 'auto',
};

// Stack for right-side buttons (sort + filter in row, legend below in map view)
const rightButtonStackStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  justifyContent: 'flex-end',
  gap: 8,
  pointerEvents: 'auto',
};

// Floating action button style
const fabStyle: CSSProperties = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 48,
  height: 48,
  padding: 0,
  border: '1px solid var(--color-border)',
  borderRadius: '50%',
  backgroundColor: 'var(--color-bg-primary)',
  color: 'var(--color-text-primary)',
  cursor: 'pointer',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  pointerEvents: 'auto',
};

// Active FAB style (when filters are active)
const fabActiveStyle: CSSProperties = {
  backgroundColor: 'var(--color-primary, #3b82f6)',
  color: 'white',
  borderColor: 'var(--color-primary, #3b82f6)',
};

// Sort pill container for positioning dropdown
const sortPillContainerStyle: CSSProperties = {
  position: 'relative',
};

// Sort pill button style
const sortPillStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  height: 36,
  padding: '0 12px',
  border: '1px solid var(--color-border)',
  borderRadius: 18,
  backgroundColor: 'var(--color-bg-primary)',
  color: 'var(--color-text-primary)',
  cursor: 'pointer',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  fontSize: '0.8125rem',
  fontWeight: 500,
  whiteSpace: 'nowrap',
};

// Sort pill active style
const sortPillActiveStyle: CSSProperties = {
  backgroundColor: 'var(--color-primary, #3b82f6)',
  color: 'white',
  borderColor: 'var(--color-primary, #3b82f6)',
};

const sortPillIconStyle: CSSProperties = {
  fontSize: '1rem',
};

const sortPillLabelStyle: CSSProperties = {
  lineHeight: 1,
};

// Legend FAB container
const legendFabContainerStyle: CSSProperties = {
  position: 'relative',
};

// Sort dropdown overlay
const sortDropdownOverlayStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 99,
};

// Sort dropdown menu
const sortDropdownStyle: CSSProperties = {
  position: 'absolute',
  top: '100%',
  right: 0,
  marginTop: 8,
  backgroundColor: 'var(--color-bg-primary, white)',
  borderRadius: 12,
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
  minWidth: 180,
  zIndex: 100,
  overflow: 'hidden',
  listStyle: 'none',
  padding: 0,
  margin: 0,
};

// Sort dropdown item
const sortDropdownItemStyle: CSSProperties = {
  display: 'block',
  width: '100%',
  padding: '12px 16px',
  fontSize: '0.875rem',
  color: 'var(--color-text-primary, #1f2937)',
  backgroundColor: 'transparent',
  border: 'none',
  textAlign: 'left',
  cursor: 'pointer',
};

// Sort dropdown item active
const sortDropdownItemActiveStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-secondary, #f3f4f6)',
  fontWeight: 600,
  color: 'var(--color-primary, #3b82f6)',
};

const emptyStateStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '50vh',
  textAlign: 'center',
  gap: 16,
};

const loadingContainerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '50vh',
};

const selectGymLinkStyle: CSSProperties = {
  color: 'var(--color-primary)',
  textDecoration: 'none',
  fontWeight: 500,
};

const emptyRoutesStyle: CSSProperties = {
  color: 'var(--color-text-secondary)',
  textAlign: 'center',
  padding: '40px 0',
};

// Welcome state styles (no gym selected)
const welcomeStateStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  textAlign: 'center',
  padding: '24px',
  gap: 16,
};

const welcomeIconStyle: CSSProperties = {
  fontSize: 64,
  marginBottom: 8,
};

const welcomeHeadingStyle: CSSProperties = {
  fontSize: 24,
  fontWeight: 600,
  color: 'var(--color-text-primary)',
  margin: 0,
};

const welcomeTextStyle: CSSProperties = {
  fontSize: 16,
  color: 'var(--color-text-secondary)',
  maxWidth: 300,
  margin: 0,
  lineHeight: 1.5,
};

const selectGymButtonStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '12px 24px',
  backgroundColor: 'var(--color-primary, #3b82f6)',
  color: 'white',
  borderRadius: 8,
  fontWeight: 500,
  fontSize: 16,
  textDecoration: 'none',
  marginTop: 8,
};

// ==================== LEGEND TOOLTIP STYLES ====================

// Tooltip container - positioned to the left of the legend button
const legendTooltipStyle: CSSProperties = {
  position: 'absolute',
  right: 60,
  top: 56, // Aligned with legend button (filter button height + gap)
  display: 'flex',
  alignItems: 'center',
  backgroundColor: 'var(--color-primary, #3b82f6)',
  color: 'white',
  padding: '10px 14px',
  borderRadius: 8,
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
  cursor: 'pointer',
  animation: 'legendTooltipPulse 2s ease-in-out infinite',
  zIndex: 25,
};

const legendTooltipTextStyle: CSSProperties = {
  fontSize: 14,
  fontWeight: 500,
  whiteSpace: 'nowrap',
};

// Arrow pointing to the legend button
const legendTooltipArrowStyle: CSSProperties = {
  position: 'absolute',
  right: -8,
  top: '50%',
  transform: 'translateY(-50%)',
  width: 0,
  height: 0,
  borderTop: '8px solid transparent',
  borderBottom: '8px solid transparent',
  borderLeft: '8px solid var(--color-primary, #3b82f6)',
};
